import { Classes, Intent } from "@blueprintjs/core";
import { DOCS_LINKS } from "@hex/common";
import { rgba } from "polished";
import React from "react";
import styled from "styled-components";

import { HexTag, HexTooltip } from "../../hex-components";
import {
  HexTooltipPopoverStylable,
  TooltipInner,
} from "../../hex-components/HexTooltip";
import { InfoIcon } from "../icons/CustomIcons";

import { DocsLink } from "./DocsLink";

export const BetaTag = styled(HexTag)`
  &&& {
    color: ${({ theme }) => theme.highlightColor};
    font-weight: ${({ theme }) => theme.fontWeight.NORMAL};

    background-color: ${({ theme }) => rgba(theme.highlightColor, 0.12)};
    border-radius: ${({ theme }) => theme.borderRadius};
  }
`;

const LegacyTag = styled(HexTag)`
  &&& {
    background-color: transparent;
    color: ${({ theme }) => theme.fontColor.MUTED};
    font-size: ${({ theme }) => theme.fontSize.SMALL};
  }
  &&&:hover {
    background-color: ${({ theme }) => theme.backgroundColor.MUTED};
  }
`;

export const R_PROJECT_LEGACY_TOOLTIP_CONTENT = (
  <span>
    R projects are now a legacy feature.{" "}
    <DocsLink to={DOCS_LINKS.RSupport}>Learn more</DocsLink>
  </span>
);

const RLegacyTooltipContent: React.FC = () => R_PROJECT_LEGACY_TOOLTIP_CONTENT;

interface RBetaTagProps {
  className?: string;
}

export const RBetaTag: React.FunctionComponent<RBetaTagProps> = ({
  className,
}) => (
  <StyledHexTooltip
    className={className}
    content={<RLegacyTooltipContent />}
    interactionKind="hover"
    placement="bottom"
  >
    <LegacyTag icon={<InfoIcon />} intent={Intent.NONE}>
      Legacy
    </LegacyTag>
  </StyledHexTooltip>
);

const StyledHexTooltip = styled(HexTooltipPopoverStylable)`
  &&& .${Classes.POPOVER_CONTENT} {
    > ${TooltipInner} {
      padding: 10px 12px;
    }
  }
`;

const RProjectLegacyTagWrapper = styled.span`
  display: inline-flex;
  align-items: center;
`;

/**
 * Legacy indicator for "Create new R project" surfaces: info icon + "Legacy" with
 * tooltip "R projects are now a legacy feature. Learn more."
 */
export const RProjectLegacyTag: React.FC<{ className?: string }> = ({
  className,
}) => (
  <StyledHexTooltip
    className={className}
    content={R_PROJECT_LEGACY_TOOLTIP_CONTENT}
    interactionKind="hover"
    placement="bottom"
  >
    <RProjectLegacyTagWrapper>
      <LegacyTag icon={<InfoIcon />} intent={Intent.NONE} small={true}>
        Legacy
      </LegacyTag>
    </RProjectLegacyTagWrapper>
  </StyledHexTooltip>
);

export const SankeyBetaTag: React.FunctionComponent<RBetaTagProps> = ({
  className,
}) => {
  return (
    <HexTooltip
      className={className}
      content="Sankey diagrams are in Beta and may have missing functionality."
      placement="bottom"
    >
      <BetaTag
        css={`
          margin: -2px 0;
        `}
        small={true}
      >
        Beta
      </BetaTag>
    </HexTooltip>
  );
};
